import { useEffect, useState } from "react";
import axios from "axios";
import { Linkedin, ExternalLink, Calendar } from "lucide-react";
import { API_BASE_URL } from "../lib/site";

const API = API_BASE_URL;

export default function LinkedInReadsPage() {
  const [posts, setPosts] = useState([]);
  const [linkedinUrl, setLinkedinUrl] = useState("https://www.linkedin.com/in/niharika-patel-04b1b4140/");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/linkedin-posts`)
      .then((r) => setPosts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));

    axios.get(`${API}/public/about-content`)
      .then((r) => {
        if (r.data?.social_linkedin) setLinkedinUrl(r.data.social_linkedin);
      })
      .catch(() => {});
  }, []);

  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  return (
    <div className="pt-16 page-enter">
      {/* Header */}
      <div className="border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 sm:py-16">
          <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">Quick Reads</p>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#0A0A0A] mb-4">LinkedIn Reads</h1>
          <p className="text-[#4B5563] max-w-lg text-lg">
            Bite-sized insights, observations and frameworks shared on LinkedIn — curated here for easy reading.
          </p>
          <a
            href={linkedinUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 mt-6 bg-[#0077B5] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#005582] transition-colors"
          >
            <Linkedin size={14} /> Follow on LinkedIn
          </a>
        </div>
      </div>

      {/* Posts Grid */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        {loading ? (
          <div className="grid md:grid-cols-2 gap-6">
            {[1,2,3,4].map((i) => <div key={i} className="border border-[#E5E7EB] h-56 bg-[#F9FAFB] animate-pulse" />)}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 text-[#9CA3AF]">LinkedIn posts coming soon.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6" data-testid="linkedin-grid">
            {posts.map((post, i) => (
              <a
                key={post.id}
                href={post.url}
                target="_blank"
                rel="noreferrer"
                className="group flex flex-col border border-[#E5E7EB] card-hover overflow-hidden"
                data-testid={`linkedin-post-${i}`}
              >
                {post.image_url && (
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={post.image_url}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-[#0077B5] text-white flex items-center justify-center flex-shrink-0">
                        <Linkedin size={13} />
                      </div>
                      {post.published_date && (
                        <span className="text-xs text-[#9CA3AF] flex items-center gap-1">
                          <Calendar size={11} /> {formatDate(post.published_date)}
                        </span>
                      )}
                    </div>
                    <ExternalLink size={14} className="text-[#D1D5DB] group-hover:text-[#002FA7] transition-colors flex-shrink-0" />
                  </div>
                  <h2 className="font-['Outfit'] font-semibold text-[#0A0A0A] text-lg leading-tight mb-3 group-hover:text-[#002FA7] transition-colors">
                    {post.title}
                  </h2>
                  <p className="text-sm text-[#4B5563] leading-relaxed line-clamp-3 flex-1">{post.excerpt}</p>
                  <div className="mt-4 pt-3 border-t border-[#F3F4F6] flex items-center gap-1 text-xs text-[#002FA7] font-medium">
                    Read on LinkedIn <ExternalLink size={11} />
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
